import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBadge } from "../StatusBadge";
import { timekeepingApi } from "../../services/timekeepingApi";
import { ScanSearch } from "lucide-react";

/** Attendance tab - historical lookup of a single employee's timecards for the shared
 * page-level period (see PeriodSelector in EmployeeProfilePage), wired into the existing
 * scan-review workflow (BLUE/RED OCR review) rather than duplicating it. There is only ONE
 * month/year control on this page now - this tab no longer keeps its own. */
export const AttendanceTab = ({ employeeId, period: payrollMonth }) => {
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    timekeepingApi.getEmployeeMonthReview(employeeId, payrollMonth)
      .then((r) => setReview(r.data))
      .catch(() => setReview(null))
      .finally(() => setLoading(false));
  }, [employeeId, payrollMonth]);

  return (
    <div className="space-y-4" data-testid="employee-attendance-tab">
      {loading && <p className="text-sm text-slate-500">Loading {payrollMonth}...</p>}

      {!loading && review && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="attendance-month-summary">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-slate-800">{payrollMonth} timecard record</p>
            <StatusBadge status={review.official_record_status} />
          </div>
          <div className="grid grid-cols-3 gap-3 text-center mb-4">
            <div className="bg-slate-50 rounded-lg p-3"><p className="text-2xl font-bold text-slate-900">{review.daily_records?.length || 0}</p><p className="text-[11px] text-slate-500 uppercase">Days recorded</p></div>
            <div className="bg-slate-50 rounded-lg p-3"><p className="text-2xl font-bold text-slate-900">{review.daily_records?.filter((d) => d.classification === "CORRECTED").length || 0}</p><p className="text-[11px] text-slate-500 uppercase">Corrections</p></div>
            <div className="bg-slate-50 rounded-lg p-3"><p className="text-2xl font-bold text-slate-900">{review.blue_file && review.red_file ? "2/2" : review.blue_file || review.red_file ? "1/2" : "0/2"}</p><p className="text-[11px] text-slate-500 uppercase">Scans linked</p></div>
          </div>
          <button
            data-testid="open-full-timecard-review-button"
            onClick={() => navigate(`/management/timekeeping/review/${employeeId}?payroll_month=${payrollMonth}`)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 active:scale-95 transition-all"
          >
            <ScanSearch size={14} /> Open Full Timecard Review (BLUE/RED scans, OCR, corrections)
          </button>
        </div>
      )}

      {!loading && !review && (
        <div className="bg-white rounded-xl border border-dashed border-slate-200 p-8 text-center" data-testid="attendance-no-data">
          <p className="text-sm text-slate-500">No timecard record found for {payrollMonth}.</p>
        </div>
      )}
    </div>
  );
};
